import { Button, Group, Paper, Progress, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconListSearch, IconPlayerPlay, IconRefresh, IconX } from '@tabler/icons-react';
import type { EnrichProgress, EnrichStage } from '../Hooks/useExportEnrichment';
import type { EnrichSummary } from '../Enrich/output';

interface Props {
  stage: EnrichStage;
  progress: EnrichProgress | null;
  summary: EnrichSummary | null;
  canStart: boolean;
  onStart: () => void;
  onCancel: () => void;
  onStartOver: () => void;
  entity?: 'request' | 'intake' | 'case' | 'registration';
  /** Rows in the "who is incomplete" list; the button only shows when this is above 0. */
  incompleteCount?: number;
  onViewIncomplete?: () => void;
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div>
      <Text size="xl" fw={700} c={warn && value > 0 ? 'orange' : undefined}>{value.toLocaleString()}</Text>
      <Text size="xs" c="dimmed">{label}</Text>
    </div>
  );
}

/** Step 4 (Request source only): fill the details the list does not carry, then enable Export. */
export function EnrichmentPanel({
  stage, progress, summary, canStart, onStart, onCancel, onStartOver, entity = 'request',
  incompleteCount = 0, onViewIncomplete,
}: Props) {
  const plural = entity === 'intake' ? 'Intakes' : entity === 'case' ? 'Cases' : entity === 'registration' ? 'Registrations' : 'Requests';
  const running = stage === 'loading' || stage === 'details' || stage === 'lookup';
  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : 0;

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <div>
            <Text fw={600}>4. Fill missing details</Text>
            <Text size="xs" c="dimmed">
              {entity === 'intake'
                ? 'You picked columns the intake list does not carry (name, email, phone, gender and date of birth of the people signing up). Press Start to look them up for every matching intake.'
                : entity === 'case'
                  ? 'You picked columns the case list does not carry (name, email, phone, gender and date of birth of the counselees). Press Start to look them up for every matching case.'
                  : entity === 'registration'
                    ? 'You picked columns the registration list does not carry (name, email, phone, gender and date of birth of the participants). Press Start to look them up for every matching registration.'
                    : 'You picked columns the request list does not carry (name of the person needing care, email, phone, gender, date of birth). Press Start to look them up for every matching request.'}
            </Text>
          </div>

          {stage === 'idle' && (
            <Button leftSection={<IconPlayerPlay size={16} />} disabled={!canStart} onClick={onStart}>
              Start
            </Button>
          )}
          {(running || stage === 'review') && (
            <Button variant="light" color="red" leftSection={<IconX size={16} />} onClick={onCancel}>
              Cancel
            </Button>
          )}
          {stage === 'done' && (
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={onStartOver}>
              Start over
            </Button>
          )}
        </Group>

        {(running || stage === 'review') && (
          <Stack gap={4}>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {stage === 'review' ? 'Waiting for your decision…' : progress?.label}
              </Text>
              {progress && (
                <Text size="xs" c="dimmed">
                  {progress.done.toLocaleString()} of {progress.total.toLocaleString()} ({pct}%)
                </Text>
              )}
            </Group>
            <Progress value={stage === 'review' ? 100 : pct} animated={running} striped={running} />
            <Text size="xs" c="dimmed">
              Filters, search, columns and sorting are locked while details are being filled. Cancel
              throws this run away and takes you back to the start with your choices kept.
            </Text>
          </Stack>
        )}

        {stage === 'done' && summary && (
          <>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              <Stat label={plural} value={summary.totalRequests} />
              <Stat label="Complete" value={summary.complete} />
              <Stat label={`People filled from the ${entity}`} value={summary.peopleFromSource2} />
              <Stat label="People filled from member records" value={summary.peopleFromSource3} />
              <Stat label="People still incomplete" value={summary.peopleIncomplete} warn />
              <Stat label="Lookups failed" value={summary.lookupsFailed} warn />
              <Stat label={`${plural} with more than 2 people`} value={summary.overTwoPeople} warn />
            </SimpleGrid>

            {incompleteCount > 0 && (
              <Group>
                <Button
                  variant="light"
                  color="orange"
                  leftSection={<IconListSearch size={16} />}
                  onClick={onViewIncomplete}
                >
                  See who is incomplete
                </Button>
              </Group>
            )}

            <Text size="xs" c="dimmed">
              Check the Missing fields and Filled from columns. Changing the filters or search discards
              these results.
            </Text>
          </>
        )}
      </Stack>
    </Paper>
  );
}
